/**
 * Rule Detail Query Policy
 *
 * Responsibilities:
 * - On init, fetch the rule by ruleId
 * - After rule loads, fetch definition + draft in parallel
 * - Dispatch loading states and data commands
 *
 * Flow:
 *   1. GET /rules/:ruleId → dispatch data/setRule
 *   2. Extract ruleDefinitionId from rule
 *   3. In parallel:
 *      a. GET /rule-definitions/:ruleDefinitionId → dispatch data/setDefinition
 *      b. POST /rules/draft { ruleDefinitionId, hierarchyLevel, hierarchyEntityId } → dispatch data/setDraftRule
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { RuleDetailState } from '../state';
import { RuleDetailCommand } from '../commands';

export const createRuleDetailQueryPolicy = (
  backend: ClientLibraryType,
  ruleId: string
): Policy<RuleDetailState, RuleDetailCommand> => ({
  name: 'rule-detail-query',

  init: ({ dispatch }) => {
    ( async () => {
      // Set initial loading
      dispatch(
        { type: 'automation/ruleDetail/loading/setRuleLoading', payload: { isLoading: true }},
        { source: 'api' }
      );

      try {
        // Step 1: Fetch the rule by ID
        const rule = await backend.queries.RulesById( ruleId );

        dispatch(
          { type: 'automation/ruleDetail/data/setRule', payload: { rule }},
          { source: 'api' }
        );

        // Step 2: Extract ruleDefinitionId and entity info
        const ruleDefinitionId = ( rule as any ).ruleDefinitionId;
        const hierarchyLevel = ( rule as any ).hierarchyLevel;
        const hierarchyEntityId = ( rule as any ).hierarchyEntityIds?.[0];

        if ( !ruleDefinitionId ) {
          console.error( '[rule-detail-query] Rule has no ruleDefinitionId' );
          return;
        }

        // Step 3: Fetch definition + draft in parallel
        dispatch(
          { type: 'automation/ruleDetail/loading/setDraftLoading', payload: { isLoading: true }},
          { source: 'api' }
        );

        const [ definitionResult, draftResponse ] = await Promise.all([
          backend.queries.RuleDefinitionsById( ruleDefinitionId ).catch( error => {
            // Built-in rules (ruleDefinitionId like 'builtIn-…') are NOT in the
            // rule-definitions readmodel, so this lookup 404s/throws for them.
            // Not fatal — the rule itself carries the merged ruleDefinition.
            console.warn(
              '[rule-detail-query] RuleDefinitionsById failed (built-in rule?); falling back to the rule\'s embedded definition:',
              error
            );
            return null;
          }),
          backend.post.rules_draft({
            ruleDefinitionId,
            hierarchyLevel,
            hierarchyEntityId
          }).catch( error => {
            console.error( '[rule-detail-query] Draft materialization failed:', error );
            return null;
          })
        ]);

        // Fall back to the rule's embedded ruleDefinition (+ categories) when the
        // separate definition lookup yields nothing — required for built-in rules
        // and any case where the rule-definitions readmodel lacks the row. Without
        // this the editor gets `definition.ruleDefinition === undefined` and the
        // form renders empty / throws.
        const ruleAny = rule as any;
        const definition =
          definitionResult && ( definitionResult as any ).ruleDefinition
            ? definitionResult
            : {
                id: ruleDefinitionId,
                ruleDefinition: ruleAny.ruleDefinition,
                categories: ruleAny.categories || []
              };

        dispatch(
          { type: 'automation/ruleDetail/data/setDefinition', payload: { definition }},
          { source: 'api' }
        );

        if ( draftResponse ) {
          const parsedDraft = Array.isArray( draftResponse ) ? draftResponse[0] : draftResponse;
          dispatch(
            { type: 'automation/ruleDetail/data/setDraftRule', payload: { draftRule: parsedDraft }},
            { source: 'api' }
          );
        } else {
          // Draft failed gracefully — clear draft loading
          dispatch(
            { type: 'automation/ruleDetail/loading/setDraftLoading', payload: { isLoading: false }},
            { source: 'api' }
          );
        }
      } catch ( error ) {
        console.error( '[rule-detail-query] Error fetching rule detail:', error );
        dispatch(
          { type: 'automation/ruleDetail/loading/setRuleLoading', payload: { isLoading: false }},
          { source: 'api' }
        );
        throw error;
      }
    })();

    // No cleanup needed for one-time fetch
    return undefined;
  }
});
